import {fuzzyFilter} from '@github-ui/fuzzy-score/fuzzy-filter'
import {hasFuzzyMatch} from '@github-ui/fuzzy-score/has-fuzzy-match'
import {AssigneePickerBase, type AssigneePickerBaseProps} from '@github-ui/item-picker-assignee'
import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import {SPECIAL_VALUES} from '@github-ui/item-picker-base/constants/placeholders'
import {VALUES} from '@github-ui/item-picker-base/constants/values'
import {LazyItemPicker} from '@github-ui/item-picker-base/LazyItemPicker'
import {useItemPickerErrorFallback} from '@github-ui/item-picker-base/useItemPickerErrorFallback'
import {QueryClient, useQueryClient} from '@github-ui/react-query'
import {useCallback, useEffect, useEffectEvent, useMemo, useRef, useState} from 'react'
import {
  apiGetSearchAssignees,
  buildAssigneePickerSearchQueryKey,
  transformSearchAssigneeResponseToQueryData,
  type AssigneePickerQueryData,
} from './api/api-get-search-assignees'
import type {Assignee} from './api/assignee'
import {parseGraphQLResponse} from '@github-ui/item-picker-base/api/graphql'
import {getActorCapabilities} from './utils/assignee-picker'

//
// ItemPicker <== ItemPickerWrapper (internal) <== ItemPickerFetcher (internal) <== AssigneePicker (exported)
//                                                                              <== AssigneeRepositoryPicker (exported)

export type AssigneePickerAssignee = Assignee

export type FetchAssigneesOptions = {
  query: string
  first?: number
  number?: number
  loginNames?: string[]
  capabilities?: string[]
}

async function defaultFetchAssignees(
  queryClient: QueryClient,
  {
    owner,
    repo,
    query,
    first,
    number,
    loginNames,
    capabilities,
  }: FetchAssigneesOptions & {
    owner: string
    repo: string
  },
): Promise<AssigneePickerQueryData> {
  // If loginNames are provided and first is not explicitly set, set first to the number of loginNames
  let effectiveFirst: number | undefined
  if (first !== undefined) {
    effectiveFirst = first
  } else if (loginNames !== undefined) {
    effectiveFirst = loginNames.length
  } else {
    effectiveFirst = undefined
  }
  // Convert loginNames array to comma-separated string for the API
  const loginNamesString = loginNames !== undefined ? loginNames.join(',') : undefined

  const response = await queryClient.fetchQuery({
    queryKey: buildAssigneePickerSearchQueryKey({
      owner,
      repo,
      query,
      first: effectiveFirst,
      number,
      loginNames: loginNamesString,
      capabilities,
    }),
    queryFn: async () => {
      const responseJson = await apiGetSearchAssignees({
        owner,
        repo,
        query,
        first: effectiveFirst,
        number,
        loginNames: loginNamesString,
        capabilities,
      })
      const responseData = parseGraphQLResponse(responseJson)
      return transformSearchAssigneeResponseToQueryData(responseData.data)
    },
  })
  return response
}

export {defaultFetchAssignees as fetchAssignees}

export type AssigneePickerProps = Omit<AssigneePickerBaseProps, 'searchAssignees' | 'viewer' | 'isLoading'> & {
  repo: string
  owner: string
  number?: number
  viewer: AssigneePickerAssignee | null
}

type AssigneeRepositoryPickerBaseProps = Omit<AssigneePickerBaseProps, 'searchAssignees' | 'viewer' | 'isLoading'> & {
  assigneeTokens: string[]
  includeAssignableBots: boolean
  includeAuthorableBots: boolean
  viewer: AssigneePickerAssignee | null
  additionalSuggestions?: AssigneePickerAssignee[]
  fetchAssignees?: typeof defaultFetchAssignees
}

export type AssigneeRepositoryPickerProps = AssigneeRepositoryPickerBaseProps & {
  repo: string
  owner: string
}

export type AssigneePickerDataSource = (options: FetchAssigneesOptions) => Promise<AssigneePickerQueryData>

export type AssigneeDataSourcePickerProps = AssigneeRepositoryPickerBaseProps & {
  dataSource: AssigneePickerDataSource
}

const DEFAULT_ADDITIONAL_SUGGESTIONS: AssigneePickerAssignee[] = []

function mergeAssigneeSuggestions(preferred: Assignee[], suggestions: Assignee[]) {
  const seenLogins = new Set<string>()
  return [...preferred, ...suggestions].filter(assignee => {
    if (seenLogins.has(assignee.login)) return false
    seenLogins.add(assignee.login)
    return true
  })
}

function filterAssigneeSuggestions(suggestions: Assignee[], query: string, fallbackLogin = '') {
  return fuzzyFilter<Assignee>({
    items: suggestions,
    filter: query,
    key: assignee => (assignee.isAgent ? assignee.displayName || assignee.login : assignee.login) || fallbackLogin,
    secondaryKey: assignee => `${assignee.name}`,
  })
}

function ItemPickerFetcherForIssue({
  repo,
  owner,
  number,
  assignees,
  suggestions,
  viewer,
  ...rest
}: AssigneePickerProps) {
  const [isLoading, setIsLoading] = useState(!suggestions || suggestions.length === 0)
  const [fetchKey, setFetchKey] = useState(0)
  const [isError, setIsError] = useState(false)
  const [data, setData] = useState<AssigneePickerQueryData | null>(null)
  const queryClient = useQueryClient()

  const hasPreloadedData = (suggestions?.length || 0) > 0

  useEffect(() => {
    if (!hasPreloadedData) {
      const fetchInitialAssignees = async () => {
        try {
          const response = await defaultFetchAssignees(queryClient, {owner, repo, query: '', number, first: 10})
          setData(response)
          setIsLoading(false)
          setIsError(false)
        } catch {
          setIsError(true)
          setIsLoading(false)
        }
      }
      fetchInitialAssignees()
    }
  }, [fetchKey, owner, queryClient, repo, number, hasPreloadedData])

  const anchorRef = useRef<HTMLButtonElement>(null)

  const {createFallbackComponent} = useItemPickerErrorFallback({
    errorMessage: LABELS.cantEditItems('assignees'),
    anchorElement: anchorProps => rest.anchorElement(anchorProps, anchorRef),
    open: true,
  })

  const searchAssignees = useCallback(
    async (query: string) => {
      try {
        setIsLoading(true)
        const response = await defaultFetchAssignees(queryClient, {owner, repo, query, number, first: 10})
        setIsLoading(false)
        setIsError(false)
        return response.assignees
      } catch {
        setIsLoading(false)
        setIsError(true)
        return []
      }
    },
    [queryClient, owner, repo, number],
  )

  const suggestedActors = suggestions || data?.assignees || []

  if (isError) {
    return createFallbackComponent(() => setFetchKey(current => current + 1))
  }

  return (
    <AssigneePickerBase
      {...rest}
      assignees={assignees}
      suggestions={suggestedActors}
      isLoading={isLoading}
      searchAssignees={searchAssignees}
      viewer={viewer}
    />
  )
}
function ItemPickerFetcherForRepository({
  repo,
  owner,
  dataSource,
  assignees,
  assigneeTokens,
  suggestions,
  additionalSuggestions = DEFAULT_ADDITIONAL_SUGGESTIONS,
  fetchAssignees = defaultFetchAssignees,
  includeAssignableBots,
  includeAuthorableBots,
  maximumAssignees,
  viewer,
  ...rest
}: AssigneeRepositoryPickerBaseProps & {
  repo?: string
  owner?: string
  dataSource?: AssigneePickerDataSource
}) {
  const shouldFetchFromAssigneeTokens = assigneeTokens.length > 0
  const assigneeTokenKey = JSON.stringify(assigneeTokens)
  const [isLoading, setIsLoading] = useState(false)
  const [resolvedAssigneeTokenKey, setResolvedAssigneeTokenKey] = useState<string | null>(
    shouldFetchFromAssigneeTokens ? null : assigneeTokenKey,
  )
  const isResolvingAssigneeTokens = shouldFetchFromAssigneeTokens && resolvedAssigneeTokenKey !== assigneeTokenKey
  const [isError, setIsError] = useState(false)
  const [fetchKey, setFetchKey] = useState(0)
  const [data, setData] = useState<AssigneePickerQueryData | null>(null)
  const [suggestedAssignees, setSuggestedAssignees] = useState<Assignee[] | null>(null)
  const [totalAssignableUsers, setTotalAssignableUsers] = useState<number | null>(null)
  const queryClient = useQueryClient()

  const capabilities = useMemo(
    () => getActorCapabilities({includeAssignableBots, includeAuthorableBots}),
    [includeAssignableBots, includeAuthorableBots],
  )
  const fetchRepositoryAssignees = useCallback(
    (options: FetchAssigneesOptions) => {
      if (dataSource) return dataSource(options)
      if (!owner || !repo) throw new Error('An assignee picker data source or repository is required')
      return fetchAssignees(queryClient, {owner, repo, ...options})
    },
    [dataSource, fetchAssignees, owner, queryClient, repo],
  )

  useEffect(() => {
    if (!shouldFetchFromAssigneeTokens) return

    let ignoreResult = false
    const fetchData = async () => {
      try {
        const response = await fetchRepositoryAssignees({
          query: '',
          loginNames: assigneeTokens,
          capabilities,
        })
        if (!ignoreResult) {
          setData(response)
          setResolvedAssigneeTokenKey(assigneeTokenKey)
          setIsError(false)
        }
      } catch {
        if (!ignoreResult) {
          setIsError(true)
        }
      }
    }
    fetchData()

    return () => {
      ignoreResult = true
    }
  }, [
    assigneeTokenKey,
    assigneeTokens,
    capabilities,
    fetchKey,
    fetchRepositoryAssignees,
    shouldFetchFromAssigneeTokens,
  ])

  const searchAssignees = useCallback(
    async (query: string) => {
      if (totalAssignableUsers && totalAssignableUsers <= VALUES.maximumSuggestedUsers && suggestedAssignees) {
        setIsLoading(false)
        return filterAssigneeSuggestions(
          mergeAssigneeSuggestions(additionalSuggestions, [
            ...suggestedAssignees,
            ...(rest.noAssigneeOption ? [SPECIAL_VALUES.noAssigneeData as Assignee] : []),
          ]),
          query,
          rest.noAssigneeOption?.source?.login,
        )
      }

      try {
        setIsLoading(true)
        const response = await fetchRepositoryAssignees({
          query,
          first: VALUES.maximumSuggestedUsers,
          capabilities,
        })
        let assignableActors = mergeAssigneeSuggestions(
          filterAssigneeSuggestions(additionalSuggestions, query),
          response.assignees,
        )

        // Store total count when query is empty
        if (response.totalCount && query.trim() === '') {
          setTotalAssignableUsers(response.totalCount)
        }

        if (rest.noAssigneeOption && hasFuzzyMatch(query, rest.noAssigneeOption?.source?.login ?? '')) {
          assignableActors = [{...SPECIAL_VALUES.noAssigneeData} as Assignee, ...assignableActors]
        }

        setIsLoading(false)
        setIsError(false)
        return assignableActors
      } catch {
        setIsLoading(false)
        setIsError(true)
        return []
      }
    },
    [
      additionalSuggestions,
      totalAssignableUsers,
      suggestedAssignees,
      rest.noAssigneeOption,
      fetchRepositoryAssignees,
      capabilities,
    ],
  )

  const suggestedActors: Assignee[] = useMemo(() => {
    let all: Assignee[] = []
    if (assignees && assignees.length > 0) {
      all = assignees
    } else if (assigneeTokens.length > 0) {
      all = data?.assignees || []
    }

    if (rest.noAssigneeOption?.selected) {
      all.unshift(SPECIAL_VALUES.noAssigneeData as Assignee)
    }

    return all
  }, [assigneeTokens.length, assignees, data?.assignees, rest.noAssigneeOption?.selected])

  const getSuggestedAssignees = useEffectEvent(() => searchAssignees(''))

  useEffect(() => {
    const loadSuggestedAssignees = async () => {
      const result = await getSuggestedAssignees()
      setSuggestedAssignees(result)
      setIsLoading(false)
    }
    loadSuggestedAssignees()
  }, [])

  const suggestedItems = useMemo(() => {
    const copilot = (suggestedAssignees || []).find(a => a.isCopilot)
    const source = mergeAssigneeSuggestions(additionalSuggestions, suggestions ? suggestions : suggestedAssignees || [])

    if (copilot && !source.some(assignee => assignee.login === copilot.login)) {
      source.unshift(copilot)
    }

    return source
  }, [additionalSuggestions, suggestedAssignees, suggestions])

  const anchorRef = useRef<HTMLButtonElement>(null)

  const {createFallbackComponent} = useItemPickerErrorFallback({
    errorMessage: LABELS.cantEditItems('assignees'),
    anchorElement: anchorProps => rest.anchorElement(anchorProps, anchorRef),
    open: true,
  })

  if (isError) {
    return createFallbackComponent(() => setFetchKey(current => current + 1))
  }

  return (
    <AssigneePickerBase
      {...rest}
      assignees={suggestedActors}
      suggestions={isResolvingAssigneeTokens ? [] : suggestedItems}
      isLoading={isLoading || isResolvingAssigneeTokens}
      searchAssignees={searchAssignees}
      maximumAssignees={maximumAssignees}
      viewer={viewer}
    />
  )
}

const getKeyBindingCommandId = (name?: string) =>
  name === 'author' ? 'item-pickers:open-author' : 'item-pickers:open-assignees'

export function AssigneePicker({
  repo,
  owner,
  number,
  anchorElement,
  readonly,
  assignees,
  suggestions,
  viewer,
  ...rest
}: AssigneePickerProps & {number: number}) {
  return (
    <LazyItemPicker
      keybindingCommandId={getKeyBindingCommandId(rest.name)}
      anchorElement={(anchorProps, ref) => anchorElement(anchorProps, ref)}
      createChild={() => (
        <ItemPickerFetcherForIssue
          repo={repo}
          owner={owner}
          number={number}
          readonly={readonly}
          assignees={assignees}
          suggestions={suggestions}
          viewer={viewer}
          triggerOpen
          anchorElement={anchorElement}
          {...rest}
        />
      )}
      insidePortal={rest.insidePortal}
    />
  )
}

export function AssigneeRepositoryPicker({anchorElement, ...props}: AssigneeRepositoryPickerProps) {
  return (
    <LazyItemPicker
      anchorElement={(anchorProps, ref) => anchorElement(anchorProps, ref)}
      keybindingCommandId={getKeyBindingCommandId(props.name)}
      createChild={() => <ItemPickerFetcherForRepository anchorElement={anchorElement} triggerOpen {...props} />}
      insidePortal={props.insidePortal}
    />
  )
}

export function AssigneeDataSourcePicker({anchorElement, dataSource, ...props}: AssigneeDataSourcePickerProps) {
  return (
    <LazyItemPicker
      anchorElement={(anchorProps, ref) => anchorElement(anchorProps, ref)}
      keybindingCommandId={getKeyBindingCommandId(props.name)}
      createChild={() => (
        <ItemPickerFetcherForRepository anchorElement={anchorElement} dataSource={dataSource} triggerOpen {...props} />
      )}
      insidePortal={props.insidePortal}
    />
  )
}
